import { useState, useRef, useEffect } from "react"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { Plus, ArrowLeft, Copy, MoreVertical, Monitor, Check, X } from "lucide-react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "@/clientWeb/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectWrapper } from "@/clientWeb/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/clientWeb/components/ui/table"
import { Button } from "@/clientWeb/components/ui/button"
import { Input } from "@/clientWeb/components/ui/input"
import { Label } from "@/clientWeb/components/ui/label"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { config } from "@/config/env"


interface TpRoom {
  id: string
  roomName: string
  noOfScreens: number
  description: string
  status: boolean
}

export default function Telepresence() {
  const t = useTranslation()
  const [rooms, setRooms] = useState<TpRoom[]>([
    { id: "1", roomName: "Main Conference Room", noOfScreens: 5, description: "Primary boardroom for executive meetings", status: true },
    { id: "2", roomName: "Meeting Room A", noOfScreens: 3, description: "Small team collaboration space", status: false },
    { id: "3", roomName: "Board Room", noOfScreens: 8, description: "Large capacity boardroom", status: true },
  ])

  const [view, setView] = useState<"list" | "links">("list")
  const [openRoom, setOpenRoom] = useState<TpRoom | null>(null)
  const [showDialog, setShowDialog] = useState(false)
  const [editRoom, setEditRoom] = useState<TpRoom | null>(null)
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null)
  const [copiedLink, setCopiedLink] = useState<string | null>(null)

  const [form, setForm] = useState({ roomName: "", roomDescription: "", roomScreens: 5 })
  // Holds translation keys, rendered with t().
  const [errors, setErrors] = useState<{ roomName?: string }>({})
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpenId(null)
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  const openAddDialog = () => {
    setEditRoom(null)
    setForm({ roomName: "", roomDescription: "", roomScreens: 5 })
    setErrors({})
    setShowDialog(true)
  }

  const openEditDialog = (room: TpRoom) => {
    setEditRoom(room)
    setForm({ roomName: room.roomName, roomDescription: room.description, roomScreens: room.noOfScreens })
    setErrors({})
    setShowDialog(true)
    setMenuOpenId(null)
  }

  const handleSave = () => {
    if (!form.roomName.trim()) {
      setErrors({ roomName: "telepresence.roomNameRequired" })
      toast.error({ title: t("telepresence.roomNameRequired"), description: t("telepresence.enterRoomName") })
      return
    }
    if (editRoom) {
      setRooms((prev) =>
        prev.map((r) =>
          r.id === editRoom.id
            ? { ...r, roomName: form.roomName, description: form.roomDescription, noOfScreens: form.roomScreens }
            : r
        )
      )
      toast.success({ title: t("telepresence.roomUpdated"), description: t("telepresence.roomUpdatedDesc") })
    } else {
      const newRoom: TpRoom = {
        id: String(Date.now()),
        roomName: form.roomName,
        description: form.roomDescription,
        noOfScreens: form.roomScreens,
        status: true,
      }
      setRooms((prev) => [...prev, newRoom])
      toast.success({ title: t("telepresence.roomAdded"), description: t("telepresence.roomCreated") })
    }
    setShowDialog(false)
  }

  const handleDelete = (id: string) => {
    setRooms((prev) => prev.filter((r) => r.id !== id))
    setMenuOpenId(null)
    toast.error({ title: t("telepresence.roomDeleted"), description: t("telepresence.roomRemoved") })
  }

  const handleToggleStatus = (id: string) => {
    const room = rooms.find(r => r.id === id)
    setRooms((prev) => prev.map((r) => (r.id === id ? { ...r, status: !r.status } : r)))
    setMenuOpenId(null)
    toast.info({ title: room?.status ? t("telepresence.roomDisabled") : t("telepresence.roomEnabled"), description: t("telepresence.statusUpdated") })
  }

  const openConferenceLinks = (room: TpRoom) => {
    setOpenRoom(room)
    setView("links")
  }

  const copyLink = (link: string) => {
    navigator.clipboard.writeText(link).then(() => {
      setCopiedLink(link)
      setTimeout(() => setCopiedLink(null), 2000)
      toast.success({ title: t("common.linkCopied"), description: t("telepresence.linkCopiedDesc") })
    })
  }

  const TP_BASE_URL = config.telepresenceUrl
  const PROJECT_ID = "inmeet-project"

  if (view === "links" && openRoom) {
    const speakerLink = `${TP_BASE_URL}/?projectid=${PROJECT_ID}&room=${openRoom.id}&mode=speaker`
    const screenLink = `${TP_BASE_URL}/?projectid=${PROJECT_ID}&room=${openRoom.id}&mode=screen`
    const participantLinks = Array.from({ length: Math.max(0, openRoom.noOfScreens - 2) }, (_, i) =>
      `${TP_BASE_URL}/?projectid=${PROJECT_ID}&room=${openRoom.id}&screen=${i + 1}`
    )

    return (
      <div className="space-y-5 animate-in fade-in duration-300">
        {/* Back header */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setView("list")}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-primary" />
          </button>
          <h2 className="text-xl font-bold text-slate-900 dark:text-foreground/80 dark:text-white">{openRoom.roomName}</h2>
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400 pl-12">{openRoom.description}</p>

        <div className="bg-card dark:bg-card border border-stroke-100 dark:border-border dark:border-border rounded-2xl p-5 shadow-xs space-y-4">
          <p className="text-sm text-slate-500 dark:text-slate-400">{t("telepresence.copyLinksInstruction")}</p>

          {/* Active Speaker Link */}
          <div>
            <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">{t("telepresence.activeSpeakerLink")}</p>
            <LinkRow link={speakerLink} copiedLink={copiedLink} onCopy={copyLink} />
          </div>

          {/* Presentation Screen Link */}
          <div>
            <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">{t("telepresence.presentationScreenLink")}</p>
            <LinkRow link={screenLink} copiedLink={copiedLink} onCopy={copyLink} />
          </div>

          {/* Participant Screen Links */}
          {participantLinks.length > 0 && (
            <div>
              <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">{t("telepresence.participantScreenLinks")}</p>
              <div className="space-y-2">
                {participantLinks.map((link, i) => (
                  <LinkRow key={i} link={link} copiedLink={copiedLink} onCopy={copyLink} label={`${t("telepresence.screen")} ${i + 1}`} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-900 dark:text-foreground/80 dark:text-white">{t("telepresence.title")}</h2>
        <button
          onClick={openAddDialog}
          className="flex items-center gap-2 bg-primary text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
        >
          <Plus className="w-4 h-4" />
          {t("telepresence.addRoom")}
        </button>
      </div>

      {/* Table */}
      <div className="bg-card dark:bg-card border border-stroke-100 dark:border-border dark:border-border rounded-2xl shadow-xs overflow-hidden">
        {rooms.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Monitor className="w-20 h-20 text-slate-200 dark:text-slate-700 mb-4" />
            <h4 className="text-base font-semibold text-slate-700 dark:text-white mb-1">{t("telepresence.noRoomsFound")}</h4>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">{t("telepresence.createFirstRoom")}</p>
            <Button onClick={openAddDialog} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              {t("telepresence.addRoom")}
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 dark:bg-card/80">
                <TableHead className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t("telepresence.roomName")}</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t("telepresence.screens")}</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t("common.status")}</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-right">{t("myFiles.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rooms.map((room) => (
                <TableRow key={room.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30">
                  <TableCell>
                    <button
                      onClick={() => openConferenceLinks(room)}
                      className="text-sm font-semibold text-primary hover:underline text-left max-w-[180px] truncate block"
                    >
                      {room.roomName}
                    </button>
                  </TableCell>
                  <TableCell className="text-sm text-slate-700 dark:text-slate-300">{room.noOfScreens}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${
                      room.status
                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400"
                        : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400"
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${room.status ? "bg-emerald-500" : "bg-slate-400"}`} />
                      {room.status ? t("common.active") : t("common.disabled")}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="relative inline-block" ref={menuOpenId === room.id ? menuRef : undefined}>
                      <button
                        onClick={() => setMenuOpenId(menuOpenId === room.id ? null : room.id)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                      >
                        <MoreVertical className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                      </button>
                      {menuOpenId === room.id && (
                        <div className="absolute right-0 top-9 bg-card dark:bg-card border border-stroke-100 dark:border-border dark:border-border rounded-xl shadow-lg z-20 min-w-[140px] py-1">
                          <button
                            onClick={() => openEditDialog(room)}
                            className="block w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                          >
                            {t("common.edit")}
                          </button>
                          <button
                            onClick={() => handleDelete(room.id)}
                            className="block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            {t("common.delete")}
                          </button>
                          <button
                            onClick={() => handleToggleStatus(room.id)}
                            className="block w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                          >
                            {room.status ? t("common.disable") : t("common.enable")}
                          </button>
                        </div>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Add / Edit Room Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editRoom ? t("telepresence.updateRoom") : t("telepresence.addRoom")}</DialogTitle>
            <DialogClose asChild>
              <button className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
                <X className="h-4 w-4" />
                <span className="sr-only">{t("common.close")}</span>
              </button>
            </DialogClose>
          </DialogHeader>

          <div className="space-y-4">
            {/* Room Name */}
            <div className="space-y-2">
              <Label htmlFor="roomName">{t("telepresence.roomName")} *</Label>
              <Input
                id="roomName"
                value={form.roomName}
                maxLength={50}
                onChange={(e) => { setForm((p) => ({ ...p, roomName: e.target.value })); setErrors({}) }}
                placeholder={t("telepresence.enterRoomName")}
              />
              {errors.roomName && <p className="text-xs text-red-500">{t(errors.roomName)}</p>}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="roomDescription">{t("common.description")}</Label>
              <textarea
                id="roomDescription"
                value={form.roomDescription}
                maxLength={500}
                onChange={(e) => setForm((p) => ({ ...p, roomDescription: e.target.value }))}
                placeholder={t("telepresence.enterDescription")}
                rows={3}
                className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring resize-none"
              />
              <p className="text-xs text-muted-foreground text-right">{form.roomDescription.length}/500</p>
            </div>

            {/* Number of Screens */}
            <SelectWrapper label={t("telepresence.numberOfScreens")}>
              <Select value={String(form.roomScreens)} onValueChange={(value) => setForm((p) => ({ ...p, roomScreens: Number(value) }))}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t("telepresence.selectScreens")} />
                </SelectTrigger>
                <SelectContent className="max-h-[300px] overflow-y-auto">
                  {Array.from({ length: 38 }, (_, i) => i + 3).map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n} {t("telepresence.screens")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </SelectWrapper>
          </div>

          <div className="flex items-center justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleSave}>
              {editRoom ? t("common.update") : t("common.add")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function LinkRow({ link, copiedLink, onCopy, label }: { link: string; copiedLink: string | null; onCopy: (l: string) => void; label?: string }) {
  const isCopied = copiedLink === link
  return (
    <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-700/50 border border-stroke-100 dark:border-border dark:border-border rounded-lg px-3 py-2">
      {label && <span className="text-xs text-slate-500 dark:text-slate-400 shrink-0">{label}</span>}
      <span className="flex-1 text-xs text-primary truncate">{link}</span>
      <button
        onClick={() => onCopy(link)}
        className="shrink-0 flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 hover:text-primary transition-colors ml-2"
      >
        {isCopied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  )
}
